import { memo, useEffect, useRef, useState } from 'react';
import { Check } from 'react-feather';
import { Skeleton } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  SegmentationDashboardWidget,
  type WidgetInstance,
  type WidgetPropsByType,
} from '@trinity/chat';
import type { WidgetMapContextValue } from '@trinity/chat-components/containers/WidgetGrid/context';
import { DEFAULT_COLORS } from '@trinity/chat-components/lib/chart-colors';
import { WidgetMediaCard } from '../../../Widget.Media/Widget.Media.Card.js';
import { WidgetQuoteCard } from '../../../Widget.Quote/Widget.Quote.Card.js';
import { ProgressBarScaleContent, ProgressBarScaleWrapper } from '../../../WidgetGrid/ProgressBarPreviewScaling.js';
import {
  SemiCircleScaleContent,
  SemiCircleScaleWrapper,
} from '../../../WidgetGrid/SemiCircleProgressPreviewScaling.js';
import { WidgetSourceInfo } from '../../../WidgetGrid/WidgetSourceInfo.js';
import type { PaletteEntry } from '../PaletteContext.js';
import { chartTypeLabel, type PickerItem, type WidgetTemplate } from './catalog.js';
import { Wireframe } from './Wireframe.js';

type CardProps = {
  selected: boolean;
  onSelect: (id: string) => void;
  /** Double-click: select and add in one step. */
  onActivate: (id: string) => void;
};

const ARROW_STEPS: ReadonlyMap<string, number> = new Map([
  ['ArrowRight', 1],
  ['ArrowDown', 1],
  ['ArrowLeft', -1],
  ['ArrowUp', -1],
]);

/** Enter adds the focused card; the arrow keys move focus to the next or previous card on the page. */
function handleCardKeyDown(
  event: React.KeyboardEvent<HTMLButtonElement>,
  id: string,
  onActivate: (id: string) => void,
): void {
  if (event.key === 'Enter') {
    event.preventDefault();
    onActivate(id);
    return;
  }
  const step = ARROW_STEPS.get(event.key);
  if (step === undefined) return;
  const page = event.currentTarget.closest('[role="dialog"]');
  const cards = [...(page?.querySelectorAll<HTMLElement>('[data-item-id]') ?? [])];
  const next = cards[cards.indexOf(event.currentTarget) + step];
  if (next) {
    event.preventDefault();
    next.focus();
  }
}

// ─── Saved widgets (library mode) ─────────────────────────────────────────────

const QuotePreview = memo(function QuotePreview(props: WidgetPropsByType[SegmentationDashboardWidget.Quote]) {
  const [, setMediaRef] = useState<HTMLMediaElement | null>(null);
  return <WidgetQuoteCard {...props} mediaSetting={props.media?.type} setMediaRef={setMediaRef} />;
});

const MediaPreview = memo(function MediaPreview(props: WidgetPropsByType[SegmentationDashboardWidget.Media]) {
  const [, setMediaRef] = useState<HTMLMediaElement | null>(null);
  return <WidgetMediaCard {...props} mediaSetting={props.media?.type ?? null} setMediaRef={setMediaRef} />;
});

const MissingPreview = (): React.ReactElement => <NoPreview>No preview</NoPreview>;

function SelectedTick(): React.ReactElement {
  return (
    <Tick>
      <Check size={14} strokeWidth={3} aria-hidden />
    </Tick>
  );
}

/**
 * The card's one control: a button laid over the whole card, so a card's preview (which may hold
 * media controls or links) is never inside it and Enter or an arrow key only acts from the button.
 */
function SelectButton({
  label,
  id,
  selected,
  onSelect,
  onActivate,
}: CardProps & { label: string; id: string }): React.ReactElement {
  return (
    <SelectButtonRoot
      type="button"
      aria-pressed={selected}
      aria-label={label}
      data-item-id={id}
      onClick={() => onSelect(id)}
      onDoubleClick={() => onActivate(id)}
      onKeyDown={(event) => handleCardKeyDown(event, id, onActivate)}
    />
  );
}

/** The progress widgets are laid out wide and scaled down, so their labels stay readable in a card. */
function ChartPreview({
  widget,
  widgetMap,
}: {
  widget: WidgetInstance;
  widgetMap: WidgetMapContextValue;
}): React.ReactElement {
  if (widget.type !== SegmentationDashboardWidget.ChartVisualization || !widget.props) return <MissingPreview />;
  const Chart = widgetMap[SegmentationDashboardWidget.ChartVisualization];
  if (!Chart) return <MissingPreview />;
  const chart = <Chart key={widget.id} {...widget.props} />;
  switch (widget.props.chart.type) {
    case 'progress-bar-widget':
      return (
        <ProgressBarScaleWrapper>
          <ProgressBarScaleContent>{chart}</ProgressBarScaleContent>
        </ProgressBarScaleWrapper>
      );
    case 'semi-circle-progress-widget':
      return (
        <SemiCircleScaleWrapper>
          <SemiCircleScaleContent>{chart}</SemiCircleScaleContent>
        </SemiCircleScaleWrapper>
      );
    default:
      return chart;
  }
}

function LibraryPreview({
  widget,
  widgetMap,
}: {
  widget: WidgetInstance;
  widgetMap: WidgetMapContextValue;
}): React.ReactElement {
  switch (widget.type) {
    case SegmentationDashboardWidget.Quote:
      return widget.props ? <QuotePreview {...widget.props} /> : <MissingPreview />;
    case SegmentationDashboardWidget.Media:
      return widget.props ? <MediaPreview {...widget.props} /> : <MissingPreview />;
    case SegmentationDashboardWidget.ChartVisualization:
      return <ChartPreview widget={widget} widgetMap={widgetMap} />;
    default:
      return <MissingPreview />;
  }
}

/** True once the node has scrolled near the viewport; a card renders its widget only then. */
function useInViewport(rootMargin: string): [React.RefObject<HTMLDivElement | null>, boolean] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin, threshold: 0.01 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [rootMargin]);

  return [ref, inView];
}

const RICH_KINDS: ReadonlySet<string> = new Set([SegmentationDashboardWidget.Quote, SegmentationDashboardWidget.Media]);

export const LibraryCard = memo(function LibraryCard({
  item,
  widget,
  widgetMap,
  selected,
  onSelect,
  onActivate,
}: CardProps & { item: PickerItem; widget: WidgetInstance; widgetMap: WidgetMapContextValue }): React.ReactElement {
  const [ref, inView] = useInViewport('600px');
  const [ready, setReady] = useState(false);
  const rich = RICH_KINDS.has(widget.type);

  // Mount the widget a beat after it scrolls into view so a fast scroll doesn't render every card.
  useEffect(() => {
    if (!inView || ready) return;
    const timer = setTimeout(() => setReady(true), 10);
    return () => clearTimeout(timer);
  }, [inView, ready]);

  const mounted = inView && ready;

  return (
    <CardRoot selected={selected}>
      <SelectButton label={item.title} id={item.id} selected={selected} onSelect={onSelect} onActivate={onActivate} />
      {selected && <SelectedTick />}
      {!rich && <CardTitle title={item.title}>{item.title}</CardTitle>}
      {!rich && <CardKind>{chartTypeLabel(item.subtype) ?? item.title}</CardKind>}
      {/* Previews are for looking at: the card's button covers them, so their own controls and links stay out of reach. */}
      <Preview ref={ref} inert>
        {mounted ? (
          <LibraryPreview widget={widget} widgetMap={widgetMap} />
        ) : (
          <Skeleton variant="rounded" width="100%" height={130} />
        )}
      </Preview>
      {widget.sourceMetadata && (
        <div inert>
          <WidgetSourceInfo sourceMetadata={widget.sourceMetadata} projectSlug={null} />
        </div>
      )}
    </CardRoot>
  );
});

// ─── Templates (new widget mode) ──────────────────────────────────────────────

export const TemplateCard = memo(function TemplateCard({
  item,
  template,
  selected,
  onSelect,
  onActivate,
}: CardProps & { item: PickerItem; template: WidgetTemplate }): React.ReactElement {
  return (
    <CardRoot template selected={selected}>
      <SelectButton label={template.label} id={item.id} selected={selected} onSelect={onSelect} onActivate={onActivate} />
      {selected && <SelectedTick />}
      <CardTitle title={template.label}>{template.label}</CardTitle>
      <CardKind>{template.description}</CardKind>
      <Preview blank>
        <Wireframe template={template} />
      </Preview>
    </CardRoot>
  );
});

// ─── Blank cards ──────────────────────────────────────────────────────────────

export const EntryCard = memo(function EntryCard({
  item,
  entry,
  selected,
  onSelect,
  onActivate,
}: CardProps & { item: PickerItem; entry: PaletteEntry }): React.ReactElement {
  return (
    <CardRoot selected={selected}>
      <SelectButton label={entry.label} id={item.id} selected={selected} onSelect={onSelect} onActivate={onActivate} />
      {selected && <SelectedTick />}
      <CardTitle title={entry.label}>{entry.label}</CardTitle>
      <CardKind>{entry.description ?? `Add an empty ${entry.label.toLowerCase()}`}</CardKind>
      <Preview blank>
        <entry.icon size={32} color="currentColor" />
      </Preview>
    </CardRoot>
  );
});

// ─── Styles ───────────────────────────────────────────────────────────────────

const CHART_VAR_COUNT = 5;

/** `--c1`…`--c5` set to the chart palette at full strength (`vivid`) or washed out (default). */
const chartVars = (vivid: boolean): Record<string, string> =>
  Object.fromEntries(
    DEFAULT_COLORS.slice(0, CHART_VAR_COUNT).map((color, i) => [`--c${i + 1}`, vivid ? color : `${color}66`]),
  );

const CardRoot = styled('div', {
  shouldForwardProp: (prop) => prop !== 'selected' && prop !== 'template',
})<{ selected: boolean; template?: boolean }>(({ theme, selected, template }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: theme.spacing(1),
  width: '100%',
  minHeight: 210,
  padding: theme.spacing(1.75),
  border: `1.5px solid ${selected ? theme.palette.primary.main : theme.palette.gray.light2}`,
  borderRadius: 16,
  color: 'inherit',
  textAlign: 'left',
  cursor: 'pointer',
  backgroundColor: selected ? `${theme.palette.primary.main}14` : theme.palette.background.paper,
  boxShadow: selected ? `0 0 0 1.5px ${theme.palette.primary.main} inset` : 'none',
  transition: 'border-color 120ms ease, box-shadow 120ms ease, transform 120ms ease',
  // Reduce layout and paint work while scrolling a long catalog.
  contentVisibility: 'auto',
  containIntrinsicSize: '210px',
  ...(template && chartVars(selected)),
  '&:hover': {
    borderColor: selected ? theme.palette.primary.main : `${theme.palette.primary.main}80`,
    boxShadow: selected ? undefined : `0 6px 18px ${theme.palette.primary.main}1F`,
    transform: 'translateY(-1px)',
    ...(template && chartVars(true)),
  },
  '&:has(> button:focus-visible)': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
    ...(template && chartVars(true)),
  },
  '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:hover': { transform: 'none' } },
}));

const SelectButtonRoot = styled('button')({
  position: 'absolute',
  inset: 0,
  zIndex: 1,
  width: '100%',
  height: '100%',
  margin: 0,
  padding: 0,
  border: 0,
  borderRadius: 'inherit',
  background: 'none',
  cursor: 'pointer',
  '&:focus-visible': { outline: 'none' },
});

const CardTitle = styled('div')({
  paddingRight: 28,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: 13,
  fontWeight: 600,
});

const CardKind = styled('div')(({ theme }) => ({
  marginTop: -4,
  fontSize: 11,
  color: theme.palette.text.secondary,
}));

const Tick = styled('span')(({ theme }) => ({
  position: 'absolute',
  top: 12,
  right: 12,
  zIndex: 2,
  pointerEvents: 'none',
  display: 'grid',
  placeItems: 'center',
  width: 22,
  height: 22,
  borderRadius: '50%',
  color: '#fff',
  backgroundColor: theme.palette.primary.main,
}));

const Preview = styled('div', {
  shouldForwardProp: (prop) => prop !== 'blank',
})<{ blank?: boolean }>(({ theme, blank }) => ({
  display: 'flex',
  flex: '1 1 auto',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  minHeight: 130,
  overflow: 'hidden',
  borderRadius: 4,
  ...(blank && {
    padding: 6,
    borderRadius: 10,
    border: `1px dashed ${theme.palette.gray.light2}`,
    backgroundColor: theme.palette.background.default,
  }),
}));

const NoPreview = styled('span')(({ theme }) => ({
  fontSize: 12,
  color: theme.palette.text.secondary,
}));
